package com.fantasytenis.shared.data.bracket.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ===== TOURNAMENT DRAW DTOs =====

@Serializable
data class TournamentDrawDto(
    val id: String,
    @SerialName("externalId") val externalId: String = "",
    @SerialName("tournamentId") val tournamentId: String = "",
    @SerialName("tournamentName") val tournamentName: String = "",
    @SerialName("seasonId") val seasonId: String = "",
    @SerialName("drawType") val drawType: Int = 1,
    val category: Int = 1,
    val gender: String = "M",
    @SerialName("drawSize") val drawSize: Int = 32,
    val status: Int = 1,
    @SerialName("seedingComplete") val seedingComplete: Boolean = false,
    @SerialName("startDate") val startDate: String? = null,
    @SerialName("endDate") val endDate: String? = null,
    val surface: String? = null,
    @SerialName("bestOf") val bestOf: Int = 3,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = ""
)

@Serializable
data class DrawPositionDto(
    val id: String,
    @SerialName("drawId") val drawId: String,
    val round: Int,
    val position: Int,
    @SerialName("matchNumber") val matchNumber: Int = 0,
    @SerialName("playerId") val playerId: String? = null,
    @SerialName("playerName") val playerName: String? = null,
    @SerialName("playerCountry") val playerCountry: String? = null,
    val seed: Int? = null,
    @SerialName("isBye") val isBye: Boolean = false,
    @SerialName("isQualifier") val isQualifier: Boolean = false,
    @SerialName("isWildcard") val isWildcard: Boolean = false,
    @SerialName("isLuckyLoser") val isLuckyLoser: Boolean = false,
    @SerialName("matchId") val matchId: String? = null,
    @SerialName("opponentPositionId") val opponentPositionId: String? = null,
    @SerialName("winnerId") val winnerId: String? = null,
    @SerialName("matchCompleted") val matchCompleted: Boolean = false,
    val score: String? = null,
    @SerialName("matchTime") val matchTime: String? = null
)

// ===== BRACKET CHALLENGE DTOs =====

@Serializable
data class ScoringRulesDto(
    @SerialName("roundPoints") val roundPoints: Map<String, Int>? = null,
    @SerialName("upsetBonusEnabled") val upsetBonusEnabled: Boolean = true,
    @SerialName("upsetBonusMultiplier") val upsetBonusMultiplier: Double = 0.5,
    @SerialName("bestOf3Modifier") val bestOf3Modifier: Double = 1.25
)

@Serializable
data class PayoutTierDto(
    @SerialName("minRank") val minRank: Int,
    @SerialName("maxRank") val maxRank: Int,
    val amount: Int? = null,
    val percentage: Double? = null
)

@Serializable
data class BracketChallengeDto(
    val id: String,
    val name: String,
    val description: String? = null,
    @SerialName("drawId") val drawId: String,
    @SerialName("tournamentName") val tournamentName: String = "",
    @SerialName("entryFee") val entryFee: Int = 0,
    @SerialName("prizePool") val prizePool: Int = 0,
    @SerialName("maxEntries") val maxEntries: Int? = null,
    @SerialName("currentEntries") val currentEntries: Int = 0,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("lockTime") val lockTime: String,
    val status: Int = 1,
    @SerialName("scoringRules") val scoringRules: String? = null, // JSON string
    @SerialName("payoutStructure") val payoutStructure: String? = null, // JSON string
    @SerialName("isFeatured") val isFeatured: Boolean = false,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = "",
    // Lock override fields for admin manual control
    @SerialName("lockOverride") val lockOverride: Int = 1, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    @SerialName("lockOverrideBy") val lockOverrideBy: String = "",
    @SerialName("lockOverrideAt") val lockOverrideAt: String = "",
    @SerialName("lockOverrideReason") val lockOverrideReason: String = "",
    @SerialName("isManuallyUnlocked") val isManuallyUnlocked: Boolean = false
)

// ===== BRACKET ENTRY DTOs =====

@Serializable
data class BracketEntryDto(
    val id: String,
    @SerialName("challengeId") val challengeId: String,
    @SerialName("userId") val userId: String,
    @SerialName("bracketName") val bracketName: String? = null,
    @SerialName("totalPoints") val totalPoints: Double = 0.0,
    @SerialName("correctPicks") val correctPicks: Int = 0,
    @SerialName("incorrectPicks") val incorrectPicks: Int = 0,
    @SerialName("pendingPicks") val pendingPicks: Int = 0,
    @SerialName("possiblePoints") val possiblePoints: Double = 0.0,
    val rank: Int? = null,
    @SerialName("payoutAmount") val payoutAmount: Int = 0,
    val status: Int = 1,
    @SerialName("submittedAt") val submittedAt: String? = null,
    @SerialName("createdAt") val createdAt: String = "",
    @SerialName("updatedAt") val updatedAt: String = ""
)

@Serializable
data class BracketPickDto(
    val id: String,
    @SerialName("entryId") val entryId: String,
    @SerialName("positionId") val positionId: String,
    val round: Int,
    @SerialName("matchNumber") val matchNumber: Int,
    @SerialName("predictedWinnerId") val predictedWinnerId: String,
    @SerialName("predictedWinnerName") val predictedWinnerName: String = "",
    @SerialName("predictedWinnerSeed") val predictedWinnerSeed: Int? = null,
    @SerialName("opponentId") val opponentId: String? = null,
    @SerialName("opponentName") val opponentName: String? = null,
    @SerialName("opponentSeed") val opponentSeed: Int? = null,
    @SerialName("matchId") val matchId: String? = null,
    @SerialName("actualWinnerId") val actualWinnerId: String? = null,
    @SerialName("isCorrect") val isCorrect: Boolean? = null,
    @SerialName("pointsEarned") val pointsEarned: Double = 0.0,
    @SerialName("isUpsetPick") val isUpsetPick: Boolean = false,
    @SerialName("upsetBonus") val upsetBonus: Double = 0.0
)

// ===== REQUEST DTOs =====

@Serializable
data class CreateBracketChallengeRequest(
    val name: String,
    val description: String? = null,
    @SerialName("drawId") val drawId: String,
    @SerialName("entryFee") val entryFee: Int,
    @SerialName("prizePool") val prizePool: Int,
    @SerialName("maxEntries") val maxEntries: Int? = null,
    @SerialName("maxEntriesPerUser") val maxEntriesPerUser: Int = 1,
    @SerialName("lockTime") val lockTime: String,
    @SerialName("scoringRules") val scoringRules: ScoringRulesDto? = null,
    @SerialName("payoutStructure") val payoutStructure: List<PayoutTierDto>? = null,
    @SerialName("isFeatured") val isFeatured: Boolean = false
)

@Serializable
data class CreateBracketEntryRequest(
    @SerialName("bracketName") val bracketName: String? = null
)

@Serializable
data class PickInputDto(
    @SerialName("positionId") val positionId: String,
    @SerialName("predictedWinnerId") val predictedWinnerId: String
)

@Serializable
data class SubmitBracketPicksRequest(
    val picks: List<PickInputDto>
)

@Serializable
data class SyncDrawRequest(
    @SerialName("seasonId") val seasonId: String
)

// ===== RESPONSE DTOs =====

@Serializable
data class BracketApiResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: T? = null
)

/**
 * Error response that can handle data as any type (including empty arrays from proto).
 * Used for parsing error responses where data might be [] instead of null.
 */
@Serializable
data class BracketErrorResponse(
    val status: Int = 0,
    val error: String? = null
    // Note: data field is omitted to allow any JSON value
)

@Serializable
data class BracketListResponse<T>(
    val status: Int = 0,
    val error: String? = null,
    val data: List<T>? = null,
    val total: Int = 0
)

@Serializable
data class BracketEntryWithPicksResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: BracketEntryDto? = null,
    val picks: List<BracketPickDto>? = null
)

@Serializable
data class SubmitPicksResponseDto(
    val status: Int = 0,
    val error: String? = null,
    val data: BracketEntryDto? = null,
    @SerialName("picksSaved") val picksSaved: Int = 0
)

@Serializable
data class SyncDrawResponseDto(
    val status: Int = 0,
    val error: String? = null,
    val data: TournamentDrawDto? = null,
    @SerialName("positionsSynced") val positionsSynced: Int = 0
)

// ===== ADMIN LOCK OVERRIDE DTOs =====

@Serializable
data class SetLockOverrideRequest(
    @SerialName("lockOverride") val lockOverride: Int, // 1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED
    val reason: String? = null
)

@Serializable
data class SetLockOverrideResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: BracketChallengeDto? = null
)

// ===== ADMIN DELETE ENTRY DTOs =====

@Serializable
data class AdminDeleteBracketEntryRequest(
    val reason: String
)

@Serializable
data class AdminDeleteBracketEntryResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: BracketEntryDto? = null,
    @SerialName("refundProcessed") val refundProcessed: Boolean = false,
    @SerialName("refundTransactionId") val refundTransactionId: String? = null
)
